//#region header */
/**************************************************************************************************
//
//  Description: Form to display the organisation information
//
//  Copyright:    © 2025 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   25.02.25 Sean Flook          GMSCM-1 Initial Revision.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

// import React, { useEffect, useRef, useState } from "react";
import React, { useEffect, useState } from "react";

import useConfigQueryStore from "../context/ConfigStore";
import useUserQueryStore from "../context/UserStore";
import useOrganisations from "./../hooks/useOrganisations";

import { MultiTenantConfigOrganisation } from "../dtos/multiTenantConfigDto";
import { OrganisationNamesDto } from "../dtos/organisationsDto";
import ConfigErrorType from "../models/configErrorType";

import { Box, Grid2 } from "@mui/material";
import ADSSelectControl from "../components/ADSSelectControl";

import { dataFormStyle, FormRowStyle } from "../utils/ADSStyles";

interface DocumentOrganisationFormProps {
  appName: string;
  data: MultiTenantConfigOrganisation;
  errors: ConfigErrorType[] | undefined;
  onDataChange: (data: MultiTenantConfigOrganisation) => void;
}

const DocumentOrganisationForm: React.FC<DocumentOrganisationFormProps> = ({ appName, data, errors, onDataChange }) => {
  const { data: organisationData, isLoading: organisationsLoading } = useOrganisations();

  const currentUser = useUserQueryStore((s) => s.userQuery.currentUser);

  const revertChanges = useConfigQueryStore((s) => s.configQuery.revertChanges);
  const originalDocument = useConfigQueryStore((s) => s.configQuery.originalDocument);
  const organisationSelected = useConfigQueryStore((s) => s.configQuery.organisationSelected);

  const [name, setName] = useState<string | undefined>();
  const [organisations, setOrganisations] = useState<OrganisationNamesDto[]>([]);

  const [nameError, setNameError] = useState<string[] | undefined>();

  /**
   * Method to update the data when a field is changed.
   *
   * @param fieldName The name of the field that has been updated.
   * @param value The new value for the field.
   */
  const updateData = (fieldName: string, value: string | number | undefined): void => {
    let updatedOrganisation: MultiTenantConfigOrganisation;
    updatedOrganisation = {
      ...data,
      [fieldName]: value,
    };

    if (onDataChange) {
      onDataChange(updatedOrganisation);
    }
  };

  /**
   * Event to handle when the name is changed.
   *
   * @param newValue The new name.
   */
  const handleNameChangeEvent = (newValue: string): void => {
    setName(newValue);
    updateData("organisationName", newValue);
  };

  useEffect(() => {
    if (organisationsLoading) return;

    const lookupNames = new Set(organisationData ?? []);
    if (data?.organisationName) {
      lookupNames.add(data.organisationName);
    }

    setOrganisations(
      Array.from(lookupNames)
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: "base" }))
        .map((x) => ({ name: x }))
    );
  }, [organisationData, organisationsLoading, data?.organisationName]);

  useEffect(() => {
    if (data) {
      setName(data.organisationName);
    }
  }, [data]);

  useEffect(() => {
    setNameError(undefined);

    if (errors && errors.length > 0) {
      for (const error of errors) {
        if (error.treeId === `${appName}|${data.organisationName}`) {
          switch (error.field) {
            case "organisationName":
              setNameError(error.errors);
              break;

            default:
              break;
          }
        }
      }
    }
  }, [errors, data, appName]);

  useEffect(() => {
    if (revertChanges && originalDocument) {
      const originalOrganisation: MultiTenantConfigOrganisation = originalDocument.organisations.find(
        (x) => x.organisationName === organisationSelected,
      ) as MultiTenantConfigOrganisation;

      if (originalOrganisation) {
        setName(originalOrganisation.organisationName);
      }
    }
  }, [revertChanges, originalDocument, organisationSelected]);

  return (
    <Box sx={dataFormStyle("OrganisationForm")}>
      <Grid2 container justifyContent="flex-start" alignItems="baseline" sx={FormRowStyle()}>
        <Grid2 size={12}>
          <ADSSelectControl
            label="Organisation"
            value={name}
            isEditable={currentUser?.isSuperAdministrator}
            lookupData={organisations}
            lookupId="name"
            lookupLabel="name"
            isRequired
            helperText="The name of the organisation."
            errorText={nameError}
            onChange={(id) => handleNameChangeEvent(id?.toString() || "")}
          />
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default DocumentOrganisationForm;
